import { useEffect, useState, type MouseEvent, type ReactNode } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import type { LiveCheckpoint } from '@/types/api'

/** Where a parked page is reached: an application run's, or a portal connection's. */
export interface LivePageSource {
  /** Distinguishes this page's queries from any other's. */
  key: readonly unknown[]
  status: () => Promise<LiveCheckpoint>
  screenshot: () => Promise<Blob>
  click: (x: number, y: number) => Promise<unknown>
  type: (text: string) => Promise<unknown>
  /** Scroll the parked page by `dy` pixels (a check below the fold). */
  scroll?: (dy: number) => Promise<unknown>
  /** Bring the CAPTCHA widget into the picture. */
  reveal?: () => Promise<unknown>
  /** The owner has finished what the page asked: carry on (for a check the worker cannot see the end of). */
  done?: () => Promise<unknown>
}

/**
 * A page the worker has parked for the user (a CAPTCHA, a human check): while
 * one is waiting this shows it live; clicks (and short typing) are relayed onto
 * the real browser, and the worker carries on by itself once the check clears —
 * the panel then simply disappears (or shows `idle`, when given).
 *
 * The picture scales to the container's width and keeps the real viewport's
 * aspect ratio; a click is mapped back through the rendered size, so it lands
 * where the finger did however small the screen.
 */
export function LivePagePanel({
  source,
  title,
  hint,
  alt,
  actions,
  idle = null,
  className = 'panel bg-warning/[0.06] px-4 py-4 sm:px-5',
  bleed = false,
}: {
  source: LivePageSource
  title: ReactNode
  hint: ReactNode
  alt: string
  /** Extra controls under the picture (a "Done" button, say). */
  actions?: ReactNode
  /** What to show while nothing is parked. */
  idle?: ReactNode
  className?: string
  /** The wrapper reaches the screen edges on phones: the picture goes edge to edge, text keeps a 16px gutter. */
  bleed?: boolean
}) {
  const live = useQuery({
    queryKey: [...source.key, 'live'],
    queryFn: source.status,
    refetchInterval: 2000,
    retry: false,
  })
  const waiting = Boolean(live.data?.waiting)
  const shot = useQuery({
    queryKey: [...source.key, 'live-shot'],
    queryFn: source.screenshot,
    enabled: waiting,
    refetchInterval: 1500,
    retry: false,
  })
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!shot.data) return
    const objectUrl = URL.createObjectURL(shot.data)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [shot.data])
  const [typed, setTyped] = useState('')
  const click = useMutation({
    mutationFn: ({ x, y }: { x: number; y: number }) => source.click(x, y),
    onError: () => undefined, // the run may have just moved on; the poll will hide us
  })
  const send = useMutation({
    mutationFn: () => source.type(typed),
    onSuccess: () => setTyped(''),
  })
  const scroll = useMutation({ mutationFn: (dy: number) => (source.scroll ? source.scroll(dy) : Promise.resolve()), onError: () => undefined })
  const reveal = useMutation({ mutationFn: () => (source.reveal ? source.reveal() : Promise.resolve()), onError: () => undefined })
  const done = useMutation({ mutationFn: () => (source.done ? source.done() : Promise.resolve()), onError: () => undefined })
  if (!waiting) return <>{idle}</>

  const width = live.data?.width ?? 1366
  const height = live.data?.height ?? Math.round((width * 9) / 16)
  const onImageClick = (event: MouseEvent<HTMLImageElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    // Scale each axis from the rendered size back to the real viewport.
    const scaleX = width / rect.width
    const scaleY = height / rect.height
    click.mutate({
      x: Math.max(0, (event.clientX - rect.left) * scaleX),
      y: Math.max(0, (event.clientY - rect.top) * scaleY),
    })
  }
  const gutter = bleed ? 'px-4 sm:px-0' : ''
  const waited = live.data?.waitedSeconds

  return (
    <section className={className} aria-live="polite">
      <div className="mx-auto w-full max-w-[920px] space-y-2.5">
        <div className={cn('flex min-w-0 items-baseline justify-between gap-3', gutter)}>
          <h2 className="min-w-0 truncate text-[14.5px] font-semibold text-ink">{title}</h2>
          {typeof waited === 'number' && (
            <span className="num shrink-0 text-[12px] text-ink-muted">{Math.round(waited)}s</span>
          )}
        </div>
        <p className={cn('truncate text-[12.5px] text-ink-muted', gutter)}>{hint}</p>

        {url ? (
          <img
            src={url}
            alt={alt}
            onClick={onImageClick}
            draggable={false}
            className={cn(
              'block h-auto w-full cursor-crosshair select-none border-y border-line bg-surface sm:rounded-field sm:border',
              click.isPending && 'opacity-80',
            )}
          />
        ) : (
          <div
            className={cn('flex w-full items-center justify-center border-y border-line bg-surface text-[13px] text-ink-muted sm:rounded-field sm:border')}
            style={{ aspectRatio: `${width} / ${height}` }}
          >
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />
              Loading the live page…
            </span>
          </div>
        )}

        {(source.scroll || source.reveal) && (
          <div className={cn('flex flex-wrap items-center gap-2', gutter)}>
            {source.reveal && (
              <Button variant="secondary" size="xs" disabled={reveal.isPending} onClick={() => reveal.mutate()}>
                Find the CAPTCHA
              </Button>
            )}
            {source.scroll && (
              <>
                <Button variant="secondary" size="xs" disabled={scroll.isPending} onClick={() => scroll.mutate(-Math.round(height * 0.6))} aria-label="Scroll up">
                  ▲ Up
                </Button>
                <Button variant="secondary" size="xs" disabled={scroll.isPending} onClick={() => scroll.mutate(Math.round(height * 0.6))} aria-label="Scroll down">
                  ▼ Down
                </Button>
              </>
            )}
            <span className="text-[12px] text-ink-faint">The check is often lower on the page — scroll to it, then click it.</span>
            {source.done && (
              <Button size="xs" className="ml-auto" disabled={done.isPending} onClick={() => done.mutate()}>
                {done.isPending ? 'Continuing…' : "I've finished — continue"}
              </Button>
            )}
          </div>
        )}
        <div className={cn('flex flex-col gap-2', gutter)}>
          <p className="text-[12px] text-ink-muted">
            {source.reveal
              ? 'Read the image above and enter its characters here. Check the text appears on the employer’s page, then use its button to continue.'
              : 'Click a field on the live page, then enter text below.'}
          </p>
          <form
            className="flex gap-2"
            onSubmit={(event) => {
              event.preventDefault()
              if (typed.trim()) send.mutate()
            }}
          >
            <input
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              maxLength={200}
              aria-label="Text to type into the page"
              placeholder={source.reveal ? 'Enter the characters you see' : 'Text for the selected field'}
              className="field-box h-11 min-w-0 flex-1 py-0 text-[16px]"
            />
            <Button type="submit" disabled={!typed.trim()} loading={send.isPending} className="shrink-0">
              Enter text
            </Button>
          </form>
          {send.isError && <p role="alert" className="text-[12px] text-danger">The text could not be entered. Select the field on the live page and try again.</p>}
          {send.isSuccess && !typed && <p role="status" className="text-[12px] text-ink-muted">Text sent to the live page. Check it above before continuing.</p>}
          {actions}
        </div>
      </div>
    </section>
  )
}
